import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Pagination,
  Typography,
  Stack,
  CircularProgress,
  Button,
  Chip,
  Grid,
  Menu,
  MenuItem,
  TableFooter,
  Divider,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
} from '@mui/material';
import { Search, Plus, Unlink, Filter, X, LayoutList, Ticket, Link, CreditCard } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import SaleDetailsModal from './SaleDetailsModal';
import AddSaleModal from './AddSaleModal';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

const last5 = (val) => (val ? String(val).slice(-5) : '');

const FullScreenDialogTransition = React.forwardRef(function FullScreenDialogTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatPaymentMethodLabel = (method) => {
  if (!method) return 'PENDING';

  const m = String(method).toLowerCase();

  if (m === 'square') return 'SQUARE';
  if (m === 'credit_card') return 'CREDIT CARD';
  if (m === 'room_charge') return 'ROOM CHARGE';
  if (m === 'cash') return 'CASH';

  // fallback genérico
  return m.replace(/_/g, ' ').toUpperCase();
};

export default function SalesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sales, setSales] = useState([]);
  const [salesDetails, setSalesDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filters, setFilters] = useState({
    saleId: '',
    localSaleId: '',
    employeeId: '',
    hotelId: '',
    status: 'ALL',
    source: '',
    paymentMethod: '', // ✅ NOVO
  });

  const renderSquareVerificationChip = (sale) => {
  if (!sale) return null;

  const method = (sale.payment_method || '').toLowerCase();
  if (!method.includes('square')) return null;

  const verified = !!(sale.square_payment_id && String(sale.square_payment_id).trim());

  if (verified) {
    return (
      <Chip
        size="small"
        icon={<Link size={16} />}
        label="LINKED"
        color="secondary"
        variant="filled"
        sx={{ fontWeight: 800 }}
      />
    );
  }

  return (
    <Chip
      size="small"
      icon={<Unlink size={16} />}
      label="UNLINKED"
      color="error"
      variant="outlined"
      sx={{ fontWeight: 800 }}
    />
  );
};

  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels] = useState([]);

  const [selectedSale, setSelectedSale] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });
  const [sortConfig, setSortConfig] = useState({ key: 'sale_time', direction: 'desc' });

  // Desktop filter menu anchor
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Mobile filter dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [viewMode, setViewMode] = useState('table');

  const perPage = 50;
  const { refreshKey } = useRefresh();

  const dateOnly = (v) => {
    if (!v) return '';
    const s = String(v);

    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) return s.split(' ')[0];

    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }

    return s;
  };

  const isSquareVerified = (sale) => !!(sale && sale.square_payment_id && String(sale.square_payment_id).trim());

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    dateFilter.startDate,
    dateFilter.endDate,
    sortConfig.key,
    sortConfig.direction,
    searchField,
    filters.saleId,
    filters.localSaleId,
    filters.employeeId,
    filters.hotelId,
    filters.status,
    filters.source,
    filters.paymentMethod, // ✅
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [empsRes, hotelsRes] = await Promise.all([api.get('/employees'), api.get('/hotels')]);
        if (cancelled) return;
        setEmployees(Array.isArray(empsRes.data) ? empsRes.data : []);
        setHotels(Array.isArray(hotelsRes.data) ? hotelsRes.data : []);
      } catch (e) {
        console.error('Error loading filters data:', e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const getStatusColor = (status) => {
    if (!status) return 'default';
    switch (String(status).toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(
      Number(amount) || 0
    );

  const fetchSalesDetails = async (salesList) => {
    setLoadingDetails(true);
    try {
      const ids = salesList.map((s) => s.id);
      const { data } = await api.post('/sales/batch-details', { ids });
      setSalesDetails(data || {});
    } catch (err) {
      console.error('Error fetching sales details (batch):', err);
      const empty = {};
      (salesList || []).forEach((s) => {
        empty[s.id] = { subtotal: 0, tip: 0, total: 0, items: [] };
      });
      setSalesDetails(empty);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: debouncedSearch,
        search_field: searchField,
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
      };

      if (dateFilter.startDate && isValid(dateFilter.startDate)) params.start_date = format(dateFilter.startDate, 'yyyy-MM-dd');
      if (dateFilter.endDate && isValid(dateFilter.endDate)) params.end_date = format(dateFilter.endDate, 'yyyy-MM-dd');

      if (filters.saleId) params.sale_id = filters.saleId;
      if (filters.localSaleId) params.local_sale_id = filters.localSaleId;
      if (filters.employeeId) params.employee_id = filters.employeeId;
      if (filters.hotelId) params.hotel_id = filters.hotelId;
      if (filters.source) params.source = filters.source;
      if (filters.paymentMethod) params.payment_method = filters.paymentMethod; // ✅ NOVO
      if (filters.status && filters.status !== 'ALL') params.status = filters.status;

      const res = await api.get('/sales', { params });

      setSales(res.data.sales || []);
      setTotalPages(res.data.pagination?.pages || 1);
      setTotalItems(res.data.pagination?.total || 0);

      if (res.data.sales?.length > 0) {
        fetchSalesDetails(res.data.sales);
      } else {
        setSalesDetails({});
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
      setSales([]);
      setSalesDetails({});
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    debouncedSearch,
    searchField,
    dateFilter.startDate,
    dateFilter.endDate,
    sortConfig.key,
    sortConfig.direction,
    filters.saleId,
    filters.localSaleId,
    filters.employeeId,
    filters.hotelId,
    filters.status,
    filters.source,
    filters.paymentMethod, // ✅
    refreshKey,
  ]);

  const openModal = (sale) => {
    setSelectedSale(sale.id);
    setModalOpen(true);
  };

  const handleSaleAdded = () => fetchSales();

  const handlePageChange = (_event, newPage) => setPage(newPage);

  const handleDateChange = (type, newDate) => setDateFilter((prev) => ({ ...prev, [type]: newDate }));

  const handleSort = (columnKey) => {
    setSortConfig((prevSort) => ({
      key: columnKey,
      direction: prevSort.key === columnKey && prevSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearAllFilters = () => {
    setSearch('');
    setSearchField('all');
    setDateFilter({ startDate: null, endDate: null });
    setFilters({
      saleId: '',
      localSaleId: '',
      employeeId: '',
      hotelId: '',
      status: 'ALL',
      source: '',
      paymentMethod: '', // ✅
    });

    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const totals = useMemo(() => {
    if (!sales?.length) return { subtotal: 0, tips: 0, total: 0 };
    return sales.reduce(
      (acc, sale) => {
        const d = salesDetails[sale.id] || { subtotal: 0, tip: 0, total: 0 };
        return {
          subtotal: acc.subtotal + (Number(d.subtotal) || 0),
          tips: acc.tips + (Number(d.tip) || 0),
          total: acc.total + (Number(d.total) || 0),
        };
      },
      { subtotal: 0, tips: 0, total: 0 }
    );
  }, [sales, salesDetails]);

  const hasActiveFilters = Boolean(
    dateFilter.startDate ||
      dateFilter.endDate ||
      (search && search.trim()) ||
      (searchField && searchField !== 'all') ||
      filters.saleId ||
      filters.localSaleId ||
      filters.employeeId ||
      filters.hotelId ||
      filters.source ||
      filters.paymentMethod || // ✅
      (filters.status && filters.status !== 'ALL')
  );

  const openFilters = (e) => {
    if (isMobile) {
      setFilterDialogOpen(true);
    } else {
      setFilterMenuAnchor(e.currentTarget);
    }
  };

  const closeFilters = () => {
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const FiltersContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Sale ID"
          value={filters.saleId}
          onChange={(e) => setFilters((prev) => ({ ...prev, saleId: e.target.value }))}
          size="small"
          fullWidth
        />
        <TextField
          label="Local ID"
          value={filters.localSaleId}
          onChange={(e) => setFilters((prev) => ({ ...prev, localSaleId: e.target.value }))}
          size="small"
          fullWidth
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Source"
          value={filters.source}
          onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
          size="small"
          fullWidth
        />
      </Box>

      {/* ✅ NOVO: Payment Method filter */}
      <FormControl size="small" fullWidth>
        <InputLabel id="payment-method-filter-label">Payment</InputLabel>
        <Select
          labelId="payment-method-filter-label"
          label="Payment"
          value={filters.paymentMethod}
          onChange={(e) => setFilters((prev) => ({ ...prev, paymentMethod: e.target.value }))}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Cash">Cash</MenuItem>
          <MenuItem value="Room Charge">Room Charge</MenuItem>
          <MenuItem value="Credit Card">Credit Card</MenuItem>
          <MenuItem value="Square">Square</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="employee-filter-label">Employee</InputLabel>
        <Select
          labelId="employee-filter-label"
          label="Employee"
          value={filters.employeeId}
          onChange={(e) => setFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
        >
          <MenuItem value="">All</MenuItem>
          {(employees || []).map((emp) => (
            <MenuItem key={emp[0]} value={String(emp[0])}>
              {emp[1]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="hotel-filter-label">Hotel</InputLabel>
        <Select
          labelId="hotel-filter-label"
          label="Hotel"
          value={filters.hotelId}
          onChange={(e) => setFilters((prev) => ({ ...prev, hotelId: e.target.value }))}
        >
          <MenuItem value="">All</MenuItem>
          {(hotels || []).map((h) => (
            <MenuItem key={h.id} value={String(h.id)}>
              {h.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider />

      <Typography variant="subtitle2" fontWeight={900}>
        Date Range
      </Typography>

      <DatePicker
        label="Start Date"
        value={dateFilter.startDate}
        onChange={(d) => handleDateChange('startDate', d)}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
      <DatePicker
        label="End Date"
        value={dateFilter.endDate}
        onChange={(d) => handleDateChange('endDate', d)}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
    </Box>
  );

  const SearchBar = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 0,
        overflow: 'hidden',
        border: '2px solid transparent',
        backgroundImage: `
          linear-gradient(#fff, #fff),
          linear-gradient(180deg, #ec4899, #a855f7)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.92)' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch" justifyContent="space-between">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch" flex={1}>
          <TextField
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sales..."
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearch('')} size="small" edge="end" sx={{ mr: -0.5 }}>
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <FormControl
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
            }}
          >
            <InputLabel id="search-field-label">Search By</InputLabel>
            <Select labelId="search-field-label" label="Search By" value={searchField} onChange={(e) => setSearchField(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="sale_id">Sale ID</MenuItem>
              <MenuItem value="local_sale_id">Local ID</MenuItem>
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="hotel">Hotel</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="source">Source</MenuItem>
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="payment_method">Payment</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={{ xs: 'space-between', md: 'flex-end' }}
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              alignItems: 'center',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto auto' },
              justifyContent: { xs: 'stretch', md: 'flex-end' },
            }}
          >
            <ToggleButtonGroup
              exclusive
              value={viewMode}
              onChange={(_e, v) => v && setViewMode(v)}
              size="small"
              sx={{
                gridColumn: { xs: '1 / span 1', md: 'auto' },
                width: { xs: '100%', md: 'auto' },
                '& .MuiToggleButton-root': {
                  borderRadius: 0,
                  px: 1.25,
                  height: 40,
                  textTransform: 'none',
                  fontWeight: 900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', md: 'auto' },
                },
              }}
            >
              <ToggleButton value="table" aria-label="Table view">
                <LayoutList size={16} />
                <span>Table</span>
              </ToggleButton>
              <ToggleButton value="ticket" aria-label="Ticket view">
                <Ticket size={16} />
                <span>Ticket</span>
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant={hasActiveFilters ? 'contained' : 'outlined'}
              onClick={openFilters}
              startIcon={<Filter size={16} />}
              size="small"
              sx={{
                height: 40,
                fontWeight: 900,
                borderRadius: 2,
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 110 },
                whiteSpace: 'nowrap',
              }}
            >
              Filters
            </Button>

            <Button
              variant="contained"
              onClick={() => setAddModalOpen(true)}
              startIcon={<Plus size={16} />}
              size="small"
              sx={{
                gridColumn: { xs: '1 / span 2', md: 'auto' },
                width: { xs: '100%', md: 'auto' },
                height: 44,
                fontWeight: 900,
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              Add Sale
            </Button>

            {hasActiveFilters && (
              <Button
                variant="text"
                color="inherit"
                onClick={clearAllFilters}
                startIcon={<X size={16} />}
                size="small"
                sx={{
                  gridColumn: { xs: '1 / span 2', md: 'auto' },
                  width: { xs: '100%', md: 'auto' },
                  height: 40,
                  fontWeight: 900,
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Stack>
      </Stack>

      {/* DESKTOP: Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={closeFilters}
        PaperProps={{
          sx: { p: 2, borderRadius: 2, width: 380, maxWidth: 420 },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography fontWeight={900}>Advanced Filters</Typography>
          <IconButton onClick={closeFilters} size="small">
            <X size={16} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {FiltersContent}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="text" color="inherit" onClick={clearAllFilters} startIcon={<X size={16} />} sx={{ fontWeight: 800 }}>
            Clear
          </Button>
          <Button variant="contained" onClick={closeFilters} sx={{ fontWeight: 900 }}>
            Done
          </Button>
        </Stack>
      </Menu>

      {/* MOBILE: Fullscreen Dialog */}
      <Dialog fullScreen open={filterDialogOpen} onClose={closeFilters} TransitionComponent={FullScreenDialogTransition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Filter size={18} />
              <Typography fontWeight={900}>Filters</Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {hasActiveFilters && (
                <Button color="inherit" onClick={clearAllFilters} sx={{ fontWeight: 900 }}>
                  Clear
                </Button>
              )}
              <Button color="inherit" onClick={closeFilters} sx={{ fontWeight: 900 }}>
                Close
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2 }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>
            Advanced Filters
          </Typography>
          {FiltersContent}

          <Box sx={{ mt: 2 }}>
            <Button fullWidth variant="contained" onClick={closeFilters} sx={{ height: 44, borderRadius: 2, fontWeight: 900 }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Dialog>
          </Box>
</Paper>
  );

  function TicketCard({ sale, details, loadingDetails, onClick }) {
    const status = sale?.status || 'PENDING';
    const verified = isSquareVerified(sale);

    const subtotal = loadingDetails ? null : (details?.subtotal ?? 0);
    const tip = loadingDetails ? null : (details?.tip ?? 0);
    const total = loadingDetails ? null : (details?.total ?? 0);
    const items = loadingDetails ? [] : (details?.items || []);
    const fmtMoney = (v) => formatCurrency(v);

    return (
      <Paper
        onClick={onClick}
        sx={{
          p: 2,
          borderRadius: 2,
          cursor: 'pointer',
          border: '1px solid #e8e8e8',
          bgcolor: '#fff',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(0,0,0,0.08)' },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 1, pb: 1, borderBottom: '1px dashed #bdbdbd' }}>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
            MINIBOSS RECEIPT
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
            {sale?.hotel || 'Hotel'}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gap: 0.5, mb: 1 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            Date: <b>{sale?.date || '-'}</b>
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            Sale ID: <b>{sale?.id}</b>
            {sale?.local_sale_id ? (
              <span>
                {' '}
                | Local: <b>{String(sale.local_sale_id).slice(-5)}</b>
              </span>
            ) : null}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            Employee: <b>{sale?.employee_name || '-'}</b>
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            Payment: <b>{sale?.payment_method || '—'}</b>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
          <Chip 
          size="small" 
          label={status} 
          color={getStatusColor(status)} 
          variant="filled" 
          sx={{ fontFamily: 'monospace', fontWeight: 800 }} />

          {sale?.payment_method?.toLowerCase()?.includes('square') && (
  isSquareVerified(sale) ? (
    <Chip
      size="small"
      icon={<Link size={16} />}
      label="LINKED"
      color="secondary"
      variant="filled"
      sx={{ fontWeight: 800 }}
    />
  ) : (
    <Chip
      size="small"
      icon={<Unlink size={16} />}
      label="UNLINKED"
      color="error"
      variant="outlined"
      sx={{ fontWeight: 800 }}
    />
  )
)}

          
        </Box>

        <Divider sx={{ borderStyle: 'dashed', borderColor: '#bdbdbd', my: 1 }} />

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 800 }}>ITEMS</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>QTY x PRICE</Typography>
          </Box>

          {loadingDetails ? (
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>Loading items...</Typography>
          ) : items.length === 0 ? (
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>No items</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              {items.map((it) => {
                const name = it.service_name || `Service #${it.service_id || ''}`.trim();
                const qty = Number(it.quantity) || 0;
                const unit = Number(it.unit_price) || 0;
                const line = it.line_total != null ? Number(it.line_total) : qty * unit;

                return (
                  <Box key={it.id ?? `${it.service_id}-${Math.random()}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '70%',
                        }}
                        title={name}
                      >
                        {String(name || '').toUpperCase()}
                      </Typography>

                      <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 800 }}>
                        {fmtMoney(line)}
                      </Typography>
                    </Box>

                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                      {qty} x {fmtMoney(unit)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed', borderColor: '#bdbdbd', my: 1 }} />

        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>SUBTOTAL</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
              {loadingDetails ? '...' : fmtMoney(subtotal)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>TIP</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
              {loadingDetails ? '...' : fmtMoney(tip)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px solid #d9d9d9' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 900 }}>TOTAL</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 900 }}>
              {loadingDetails ? '...' : fmtMoney(total)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 1, pt: 1, borderTop: '1px dashed #bdbdbd' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>Thank you!</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
          {SearchBar}

          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CircularProgress size={22} />
                <Typography fontWeight={800}>Loading sales…</Typography>
              </Paper>
            ) : sales.length === 0 ? (
              <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
                <Typography fontWeight={900}>No sales found</Typography>
                {hasActiveFilters && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Try clearing filters.
                  </Typography>
                )}
              </Paper>
            ) : viewMode === 'ticket' ? (
              <>
                <Grid container spacing={2}>
                  {sales.map((sale) => {
                    const details = salesDetails[sale.id] || { subtotal: 0, tip: 0, total: 0, items: [] };
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={sale.id}>
                        <TicketCard sale={sale} details={details} loadingDetails={loadingDetails} onClick={() => openModal(sale)} />
                      </Grid>
                    );
                  })}
                </Grid>

                <Paper
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Showing {sales.length} of {totalItems} sales
                      {hasActiveFilters && <span style={{ fontWeight: 900 }}> (filtered)</span>}
                    </Typography>
                    <Chip label={`TOTAL: ${formatCurrency(totals.total)}`} color="primary" sx={{ fontWeight: 900 }} />
                  </Stack>

                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
                </Paper>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    backgroundColor: '#fff',
                  }}
                >
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, overflowX: 'visible', minWidth: '100%' }}>
                    <Table
                      stickyHeader
                      sx={{
                        minWidth: 1120, // um pouco maior pq adicionamos Payment + Verified
                        '& th, & td': {
                          whiteSpace: 'nowrap',
                          fontSize: { xs: 12, sm: 13 },
                          py: { xs: 1, sm: 1.25 },
                        },
                        '& th': { fontWeight: 900, backgroundColor: '#f8f9fa' },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={sortConfig.key === 'sale_time'}
                              direction={sortConfig.direction}
                              onClick={() => handleSort('sale_time')}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel active={sortConfig.key === 'id'} direction={sortConfig.direction} onClick={() => handleSort('id')}>
                              Sale ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Local ID</TableCell>
                          <TableCell>Employee</TableCell>
                          <TableCell>Hotel</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Payment</TableCell>
                          <TableCell>Square Link</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                          <TableCell align="right">Tip</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {(sales || []).map((sale) => {
                          const details = salesDetails[sale.id] || { subtotal: 0, tip: 0, total: 0 };
                          const verified = isSquareVerified(sale);

                          return (
                            <TableRow
                              hover
                              key={sale.id}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9fbff' } }}
                              onClick={() => openModal(sale)}
                            >
                              <TableCell>{dateOnly(sale.date || sale.sale_date || sale.sale_time_local || sale.sale_time) || '—'}</TableCell>
                              <TableCell>{sale.id}</TableCell>
                              <TableCell>{sale.local_sale_id ? last5(sale.local_sale_id) : '—'}</TableCell>
                              <TableCell>{sale.employee_name || sale.employee || '—'}</TableCell>
                              <TableCell>{sale.hotel_name || sale.hotel || '—'}</TableCell>
                              <TableCell>
                                <Chip 
                                size="small" 
                                variant={sale.status === 'PENDING' ? 'outlined' : 'filled'}
                                label={sale.status} 
                                color={getStatusColor(sale.status)} 
                                sx={{ fontWeight: 800 }}
                               
                                />
                                
                                
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={formatPaymentMethodLabel(sale.payment_method)}
                                  variant={sale.payment_method ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 800 }}
                                  color={sale.payment_method ? 'primary' : 'warning'}
                                />
                              </TableCell>

                              <TableCell>
                        {renderSquareVerificationChip(sale)}
                              </TableCell>

                              <TableCell align="right">{loadingDetails ? '...' : formatCurrency(details.subtotal)}</TableCell>
                              <TableCell align="right">{loadingDetails ? '...' : formatCurrency(details.tip)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 900 }}>
                                {loadingDetails ? '...' : formatCurrency(details.total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>

                      {sales.length > 0 && !loadingDetails && (
                        <TableFooter>
                          <TableRow
                            sx={{
                              backgroundColor: '#f8f9fa',
                              '& td': { borderTop: '2px solid #dee2e6', fontWeight: 900 },
                            }}
                          >
                            <TableCell colSpan={8} align="right" sx={{ color: '#495057' }}>
                              Total ({sales.length} sales):
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#495057' }}>
                              {formatCurrency(totals.subtotal)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#495057' }}>
                              {formatCurrency(totals.tips)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#1976d2', fontSize: '0.95rem' }}>
                              {formatCurrency(totals.total)}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  mt={2}
                  width="100%"
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {sales.length} of {totalItems} sales
                    {hasActiveFilters && <span style={{ fontWeight: 900 }}> (filtered)</span>}
                  </Typography>
                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded" />
                </Stack>
              </>
            )}

            <SaleDetailsModal
              open={modalOpen}
              onClose={() => {
                setModalOpen(false);
                fetchSales();
              }}
              saleId={selectedSale}
            />

            <AddSaleModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onSaleAdded={handleSaleAdded} />
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
